import { AppEnvSetting, AppLogo, DefaultHeaderConfig, HOTEL_CONFIG, appDisplayDateFormat } from '@/config';
import { useAppContext } from '@/contexts/AppContext';
import { AppUrls, generateHotelDetailUrl } from '@/urls';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faClockRotateLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { DropdownMenu, DropdownToggle, PopoverBody, UncontrolledDropdown, UncontrolledPopover } from 'reactstrap';
import HotelJapaneseName from '../Hotels/HotelJapaneseName';
import { formatPriceWithCurrency } from '@/hooks/useCurrencyFormat';
import { useGetBookingHistory, useGetBookmarkHotels } from '@/hooks/dataProvider';
import HotelStar from '../Hotels/HotelStar';
import { useTranslation } from 'react-i18next';
import authService from '@/services/authService';
import { useGetUseRole } from '@/hooks/useGetUserRole';

export default function DefaultHeader({ isSticky }: { isSticky?: boolean }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { data: bookmarkHotels } = useGetBookmarkHotels(8);
  const bookingRef = React.useRef<any>();
  const bookmarkRef = React.useRef<any>();
  const { isAdmin, isBooking } = useGetUseRole();
  const { data: bookings } = useGetBookingHistory(10);
  useEffect(() => {
    const body = document.querySelector('body');
    if (body) {
      if (isOpen) {
        body.classList.add('-is-sidebar-open');
      } else {
        body.classList.remove('-is-sidebar-open');
      }
    }
  }, [isOpen]);

  const onHeaderLinkClick = (event, urlConfig: typeof DefaultHeaderConfig.urls[1]) => {
    if (urlConfig.isSsoUrl) {
      authService.generateTokenAndRedirect2Miyabi(urlConfig.url, urlConfig.target === '_blank', event);
    }
  };
  return (
    <>
      <header className={`header ${isSticky ? 'is-sticky bg-white' : 'bg-white'}`}>
        <div className="header__container px-30 sm:px-20">
          <div className="row justify-between items-center">
            <div className="col-auto">
              <div className="d-flex items-center">
                <Link to={AppUrls.HomePage} className="header-logo">
                  <AppLogo />
                </Link>
                <div className="header-menu pl-50">
                  <div className="header-menu__content">
                    <nav className="menu js-navList">
                      <ul className={`menu__nav text-dark-1  -is-active`}>
                        {DefaultHeaderConfig.urls.map(url => {
                          return (
                            <li key={url.text}>
                              {url?.isExternalUrl ? (
                                <a
                                  href={url.url}
                                  rel="noopener noreferrer"
                                  className="text-18"
                                  target={url.target}
                                  onClick={e => onHeaderLinkClick(e, url as any)}
                                >
                                  {url.text}
                                </a>
                              ) : (
                                <Link to={url.url} className="text-18" target={url.target} onClick={e => onHeaderLinkClick(e, url as any)}>
                                  {url.text}
                                </Link>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
            {/* End .col-auto */}

            <div className="col-auto">
              <div className="d-flex items-center">
                <div className="header-menu">
                  <div className="header-menu__content">
                    <nav className="menu js-navList">
                      <div className={`d-flex items-center`}>
                        {DefaultHeaderConfig.displayBookmark ? (
                          <>
                            <a ref={bookmarkRef} className="text-16 mr-20 cursor-pointer">
                              <FontAwesomeIcon icon={faHeart} className=" text-20 pr-10 text-red-2" />
                              {t('headerCpn_Bookmarks')}
                            </a>
                            <UncontrolledPopover
                              trigger="legacy"
                              className="default-popover w-420px shadow"
                              target={bookmarkRef}
                              placement="bottom-start"
                            >
                              <PopoverBody>
                                {bookmarkHotels?.data?.map(hotel => {
                                  return (
                                    <Link
                                      to={generateHotelDetailUrl(hotel.hotelName, hotel.hotelCode)}
                                      className="row p-2 x-gap-10 y-gap-5 d-flex items-center text-14"
                                      key={hotel.hotelCode}
                                    >
                                      <div className="col-2">
                                        {hotel?.images?.length ? (
                                          <img className="w-100 aspect-ratio-1-1 col-12 rounded-4" src={hotel?.images[0]} alt="hotel" />
                                        ) : (
                                          <img className="w-100 aspect-ratio-1-1 col-12 rounded-4" src={HOTEL_CONFIG.defaultImage} alt="hotel" />
                                        )}
                                      </div>
                                      <div className="col">
                                        <div className="fw-500">
                                          {hotel.hotelName}
                                          <HotelJapaneseName name={hotel.hotelNameJp} addBefore="-" />
                                        </div>
                                        <div className="d-flex items-center">
                                          <HotelStar star={hotel.starRating || 0} />
                                        </div>
                                      </div>
                                    </Link>
                                  );
                                })}
                                <Link
                                  to={AppUrls.AgentBookmarks}
                                  className="button mt-20 mb-10 w-100 py-15 bg-blue-1 text-white fw-500 rounded-2 text-16"
                                >
                                  {t('headerCpn_SeeAllHotels')}
                                  <i className="icon-arrow-top-right ml-15" />
                                </Link>
                              </PopoverBody>
                            </UncontrolledPopover>
                          </>
                        ) : null}

                        {DefaultHeaderConfig.displayBookingHistory && (isAdmin || isBooking) ? (
                          <>
                            <a className="text-16 mr-20 cursor-pointer" ref={bookingRef}>
                              <FontAwesomeIcon icon={faClockRotateLeft} className=" text-20 pr-10 text-dark-4 " />
                              {t('headerCpn_BookingHistory')}
                            </a>
                            <UncontrolledPopover
                              trigger="legacy"
                              className="default-popover w-420px shadow"
                              target={bookingRef}
                              placement="bottom-start"
                            >
                              <PopoverBody>
                                {bookings?.map(booking => {
                                  let checkinDate = appDisplayDateFormat.default.dateOnly(booking.checkinDate);
                                  let checkoutDate = appDisplayDateFormat.default.dateOnly(booking.checkoutDate);

                                  return (
                                    <Link
                                      to={generateHotelDetailUrl(booking.hotelName, booking.hotelCode)}
                                      className="row p-2 x-gap-10 y-gap-5 d-flex items-center text-14 border-bottom"
                                      key={booking.id}
                                    >
                                      <div className="col">
                                        <div className="fw-500 ">{booking.hotelName}</div>
                                        <div className="text-12  items-center ">
                                          <span className="mr-5 fw-500">{booking.refNumber}</span>
                                          <div className="d-flex items-center">
                                            <i className="ficon ficon-check-in mr-5 text-dark-1" />
                                            {checkinDate}
                                            <FontAwesomeIcon icon={faArrowRight} className=" text-16 pr-10 text-dark-1 ml-5 mr-5" />
                                            <i className="ficon ficon-check-out  mr-5 text-dark-1" />
                                            {checkoutDate}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-auto fw-600 text-dark-2">
                                        {formatPriceWithCurrency(booking.sellingPrice, booking.currency)}
                                      </div>
                                    </Link>
                                  );
                                })}
                                <Link
                                  to={AppUrls.AgentBookings}
                                  className="button mt-20 mb-10 w-100 py-15 bg-blue-1 text-white fw-500 rounded-2 text-16"
                                >
                                  {t('headerCpn_SeeAllBooking')}
                                  <i className="icon-arrow-top-right ml-15" />
                                </Link>
                              </PopoverBody>
                            </UncontrolledPopover>
                          </>
                        ) : null}

                        <UncontrolledDropdown className="shadow transition-cubic rounded-100 p-0">
                          <DropdownToggle tag="button">
                            <img
                              src="/static/images/icons/person.svg"
                              alt="user"
                              style={{ border: '2px solid #3554d1', padding: 5 }}
                              className="icon-user size-40 rounded-full object-cover"
                            />
                          </DropdownToggle>
                          <DropdownMenu className="w-200px">
                            <div className="px-10 py-20">
                              <div className="pb-10">
                                <Link to={AppUrls.AgentDashboard} style={{ justifyContent: 'flex-start' }}>
                                  <img src="/static/images/icons/compass.svg" alt="Agent Portal" className=" text-20 px-15 text-dark-1 " />
                                  {t('headerCpn_AgentPortal')}
                                </Link>
                              </div>

                              <div>
                                <Link to={AppUrls.Logout} style={{ justifyContent: 'flex-start' }}>
                                  <img src="/static/images/icons/log-out.svg" alt="Logout" className="  px-15 text-dark-1" />
                                  {t('base.Logout')}
                                </Link>
                              </div>
                            </div>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>
                    </nav>
                  </div>
                </div>

                <div className="d-none xl:d-flex x-gap-20 items-center pl-20">
                  <div>
                    <button onClick={() => setIsOpen(true)} className="d-flex items-center icon-menu text-20"></button>
                  </div>

                  <div className={`offcanvas offcanvas-start ${isOpen ? 'show' : ''} mobile_menu-contnet `}>
                    <div className="pro-header d-flex align-items-center justify-between border-bottom-light">
                      <AppLogo style={{ height: 30 }} />
                      <div className="fix-icon" data-bs-dismiss="offcanvas" aria-label="Close" onClick={() => setIsOpen(false)}>
                        <i className="icon icon-close"></i>
                      </div>
                      {/* icon close */}
                    </div>
                    <div className="px-10 py-20">
                      <ul>
                        {DefaultHeaderConfig.urls.map(url => {
                          return (
                            <li className="py-5" key={url.text}>
                              {url?.isExternalUrl ? (
                                <a
                                  href={url.url}
                                  rel="noopener noreferrer"
                                  className="text-18"
                                  target={url.target}
                                  onClick={e => onHeaderLinkClick(e, url as any)}
                                >
                                  {url.text}
                                </a>
                              ) : (
                                <Link to={url.url} className="text-18" target={url.target} onClick={e => onHeaderLinkClick(e, url as any)}>
                                  {url.text}
                                </Link>
                              )}
                            </li>
                          );
                        })}
                        <li className="py-5">
                          <Link className="text-18" to={AppUrls.AgentDashboard}>
                            {t('headerCpn_AgentPortal')}
                          </Link>
                        </li>

                        <li className="py-5">
                          <Link className="text-18" to={AppUrls.Logout}>
                            {t('base.Logout')}
                          </Link>
                        </li>
                      </ul>
                    </div>
                    <div className="border-top-light pt-20">
                      <div className="pt-60 pb-60">
                        <div className="row y-gap-40 justify-between xl:justify-start">
                          <div className="col-12">
                            <label className="text-16 fw-500 mb-10">For any problem regarding online hotel booking</label>
                            <a href="mailto:omega_support@nta.co.jp" target="_blank" className="d-block text-18 fw-500 mb-10 fw-bold">
                              omega_support@nta.co.jp
                            </a>
                          </div>
                        </div>
                        <div className="row y-gap-40 justify-between xl:justify-start">
                          <div className="col-xl-2 col-lg-4 col-sm-6">
                            <a href="https://www.ntainbound.com/" target="_blank" className="d-block text-16 fw-500 ">
                              About Us
                            </a>
                          </div>
                          <div className="col-xl-2 col-lg-4 col-sm-6">
                            <a href="https://www.ntainbound.com/about-japan" target="_blank" className="d-block text-16 fw-500">
                              About Japan
                            </a>
                          </div>

                          <div className="col-xl-2 col-lg-4 col-sm-6">
                            <a href="https://www.ntainbound.com/privacy-policy/" target="_blank" className="d-block text-16 fw-500">
                              {t('footerCpn_Privacy')}
                            </a>
                          </div>
                          <div className="col-xl-2 col-lg-4 col-sm-6">
                            <a
                              href="https://inbound.ntainbound.com/Booking/inbound/en/static/AgentPolicy.htm"
                              target="_blank"
                              className="d-block text-16 fw-500"
                            >
                              {t('footerCpn_Terms')}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
